import api, { armorAPI } from '~/services/api';
import { Asset, JSONAsset, toJSON as assetToJSON } from '~/types/Asset';
import { JSONBrand } from '~/types/Brand';
import { JSONCompany } from '~/types/Company';
import { JSONIDVerification } from '~/types/IDVerification';
import { JSONUser, ProfileUser } from '~/types/User';
import { decamelizeKeys } from '~/utils/humps';

function getCurrentUser() {
  return api.request<JSONUser>({
    url: 'v1/ui/account',
    method: 'GET',
  });
}

function updateCompany(payload: JSONCompany) {
  return armorAPI.request<JSONCompany>({
    url: '/setting/company',
    method: 'PUT',
    headers: {},
    data: payload,
  });
}

function fetchCompany() {
  return armorAPI.request<JSONCompany>({
    url: '/setting/company/',
    method: 'GET',
  });
}

function updateCreditCard(
  tokenId: string,
  billingAddress: string,
  trackerSessionId: string,
  isImpersonate: string,
) {
  return api.request({
    url: 'v1/billing/credit_card',
    method: 'PUT',
    data: {
      stripe_token: tokenId,
      billing_address: billingAddress,
      tracker_sessionid: trackerSessionId,
      is_impersonate: isImpersonate,
    },
  });
}

function fetchAssets() {
  return api.request<{ results: JSONAsset[] }>({
    url: '/ui/assets',
    method: 'GET',
  });
}

function uploadAssetDraft(assetObjectId: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);

  return api.request({
    url: `/ui/assets/${assetObjectId}/draft_image`,
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  });
}

function publishAssetDraft(assetObjectId: string) {
  return api.request({
    url: `/ui/assets/${assetObjectId}/draft_image/publish`,
    method: 'POST',
  });
}

function clearAssetDraft(assetObjectId: string) {
  return api.request({
    url: `/ui/assets/${assetObjectId}/draft_image/clear`,
    method: 'POST',
  });
}

function revertAssetDraft(assetObjectId: string) {
  return api.request({
    url: `/ui/assets/${assetObjectId}/draft_image`,
    method: 'DELETE',
  });
}

function uploadAndPublishAsset(assetType: string, file: File) {
  const formData = new FormData();
  formData.append('asset_type', assetType);
  formData.append('rank', '1');
  formData.append('file', file);
  return api.request({
    url: '/ui/assets/upload_and_publish',
    method: 'POST',
    data: formData,
  });
}

function updateAsset(asset: Asset) {
  return api.request<JSONAsset>({
    url: `/ui/assets/${asset.objectId}`,
    method: 'PUT',
    data: assetToJSON(asset),
  });
}

function createAsset(asset: OptionalObjectId<Asset>) {
  return api.request<JSONAsset>({
    url: '/ui/assets',
    method: 'POST',
    data: assetToJSON(asset),
  });
}

function createVerificationIntent(returnUrl: string) {
  return armorAPI.request<{ redirect_url: string }>({
    url: '/id_verification',
    method: 'POST',
    data: {
      return_url: returnUrl,
    },
  });
}

function getVerificationStatus() {
  return armorAPI.request<JSONIDVerification>({
    url: '/id_verification',
    method: 'GET',
  });
}

function fetchBrand() {
  return api.request<JSONBrand>({
    url: '/ui/account/brand',
    method: 'GET',
  });
}

function updateBrandColor(brandColor: string) {
  return api.request({
    url: '/ui/account/brand',
    method: 'PUT',
    data: {
      brand_primary_color: brandColor,
    },
  });
}

function updateUser(data: ProfileUser, isImpersonate: string) {
  return api.request({
    url: 'ui/user/profile',
    method: 'PUT',
    data: {
      ...decamelizeKeys(data),
      is_impersonate: isImpersonate,
    },
  });
}

function addUser(email?: string) {
  return api.request({
    url: '/ui/account/login_users',
    method: 'POST',
    data: {
      email,
    },
  });
}

function sendInvite(id: string | number) {
  return api.request({
    url: `/ui/account/login_users/${id}/send_invitation_email`,
    method: 'POST',
  });
}

function deleteUser(id: string | number) {
  return api.request({
    url: `/ui/account/login_users/${id}`,
    method: 'DELETE',
  });
}

function getLoginUsers() {
  return api.request({
    url: '/ui/account/login_users?results=200',
    method: 'GET',
  });
}

export {
  updateUser,
  getCurrentUser,
  updateCompany,
  fetchCompany,
  updateCreditCard,
  fetchAssets,
  uploadAssetDraft,
  publishAssetDraft,
  clearAssetDraft,
  revertAssetDraft,
  uploadAndPublishAsset,
  updateAsset,
  createAsset,
  createVerificationIntent,
  getVerificationStatus,
  fetchBrand,
  updateBrandColor,
  getLoginUsers,
  addUser,
  sendInvite,
  deleteUser,
};
